import { useEffect } from "react";
import { Box, Typography, IconButton, styled } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { hideToast } from "@/redux/actions/toast-actions";

const ToastRoot = styled(Box)(({ theme, variant }) => ({
  position: "fixed",
  top: 24,
  right: 24,
  zIndex: 9999,
  minWidth: 300,
  maxWidth: 420,
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  borderRadius: 0,
  boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
  backgroundColor: theme.palette.background.paper,
  borderLeft: `4px solid ${
    variant === "success"
      ? "#16a34a"
      : variant === "error"
        ? "#dc2626"
        : "#2563eb"
  }`,
  animation: "slideIn 0.25s ease",
  "@keyframes slideIn": {
    from: { opacity: 0, transform: "translateX(40px)" },
    to: { opacity: 1, transform: "translateX(0)" },
  },
}));

const iconMap = {
  success: <CheckCircleOutlineIcon sx={{ color: "#16a34a", mt: 0.2 }} />,
  error: <ErrorOutlineIcon sx={{ color: "#dc2626", mt: 0.2 }} />,
  info: <InfoOutlinedIcon sx={{ color: "#2563eb", mt: 0.2 }} />,
};

const CustomToast = ({
  open,
  message,
  variant = "info",
  onClose,
  duration = 4000,
}) => {
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [open, duration, onClose]);

  if (!open) return null;

  return (
    <ToastRoot variant={variant}>
      {iconMap[variant]}
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ textTransform: "capitalize" }}
        >
          {variant}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      </Box>
      <IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </ToastRoot>
  );
};

const ToastWrapper = () => {
  const dispatch = useDispatch();
  const { open, message, variant } = useSelector((state) => state.toast);

  return (
    <CustomToast
      open={open}
      message={message}
      variant={variant}
      onClose={() => dispatch(hideToast())}
    />
  );
};

export default ToastWrapper;
